import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CURRENCY_SYMBOLS } from '@/lib/currencyConversion';
import { useLanguage } from '@/contexts/LanguageContext';

const presets = [1000, 2000, 3000, 4000];

interface StepBudgetProps {
  currency: string;
  onNext: (budget: number) => void;
  onSkip: () => void;
}

export const StepBudget = ({ currency, onNext, onSkip }: StepBudgetProps) => {
  const { t } = useLanguage();
  const [budget, setBudget] = useState(1000);
  const [customMode, setCustomMode] = useState(false);
  
  const currencySymbol = CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS] || currency;
  const dailyBudget = Math.round(budget / 30);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center space-y-3">
        <h2 className="text-2xl-adhd font-bold">{t('onboarding.budget.title')}</h2>
        <p className="text-muted-foreground text-base">{t('onboarding.budget.subtitle')}</p>
      </div>

      {/* Dynamic daily breakdown - prominent display */}
      <div className="text-center py-4 px-4 bg-primary/10 rounded-xl border border-primary/20">
        <p className="text-lg font-semibold text-primary">
          {currencySymbol}{budget.toLocaleString()}/month
        </p>
        <p className="text-sm text-primary/80 mt-1">
          ≈ {currencySymbol}{dailyBudget}/day to spend
        </p>
      </div>

      {!customMode ? (
        <div className="grid grid-cols-2 gap-3">
          {presets.map((preset) => (
            <Button
              key={preset}
              variant={budget === preset ? 'default' : 'outline'}
              onClick={() => setBudget(preset)}
              className={`min-h-touch text-lg transition-all ${budget === preset ? 'shadow-lg scale-105' : ''}`}
            >
              {currencySymbol}{preset}
            </Button>
          ))}
        </div>
      ) : (
        <Input
          type="number"
          value={budget}
          onChange={(e) => setBudget(Number(e.target.value))}
          className="text-xl-adhd min-h-touch text-center"
          autoFocus
        />
      )}

      <Button
        variant="ghost"
        onClick={() => setCustomMode(!customMode)}
        className="w-full"
      >
        {customMode ? t('onboarding.budget.showPresets') : t('onboarding.budget.custom')}
      </Button>

      <div className="space-y-2">
        <Button onClick={() => onNext(budget)} className="w-full min-h-touch">
          {t('onboarding.budget.next')}
        </Button>
        <button 
          onClick={onSkip}
          className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
        >
          {t('onboarding.budget.skip')}
        </button>
      </div>
    </div>
  );
};
